import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    Sparkles, Swords, Trophy, MapPin, Users, Zap, Heart, Target,
    Star, Shield, Coins, Book, Package, Wand2, Crown, Gift,
    ChevronRight, Play, Home
} from 'lucide-react';
import CustomCursor from './CustomCursor';

const Features = () => {
    const mainFeatures = [
        {
            icon: <Sparkles className="w-12 h-12" />,
            title: "900+ Pokémon Disponibles",
            description: "Tous les Pokémon des générations 1 à 9, incluant les formes régionales, méga-évolutions et Pokémon légendaires",
            color: "from-yellow-400 to-orange-500",
            details: [
                "Formes Alola et Galar",
                "Méga-évolutions complètes",
                "Pokémon Shinies avec taux augmenté",
                "Formes alternatives (Unown, Vivillon...)"
            ]
        },
        {
            icon: <Swords className="w-12 h-12" />,
            title: "Combat Compétitif",
            description: "Système de combat fidèle aux jeux officiels avec mécaniques avancées et stratégie approfondie",
            color: "from-red-500 to-pink-600",
            details: [
                "EVs et IVs personnalisables",
                "Talents cachés et capacités spéciales",
                "Objets de combat (Choix, Focus Sash...)",
                "Méta-game compétitif actif"
            ]
        },
        {
            icon: <MapPin className="w-12 h-12" />,
            title: "Monde Gigantesque",
            description: "Explore un monde vaste avec des biomes uniques, des donjons mystérieux et des zones secrètes",
            color: "from-green-500 to-emerald-600",
            details: [
                "40+ biomes différents",
                "Donjons avec boss légendaires",
                "Zones cachées à découvrir",
                "Téléportations rapides"
            ]
        },
        {
            icon: <Trophy className="w-12 h-12" />,
            title: "Arènes & Ligue",
            description: "Affronte les champions d'arène et deviens le maître de la Ligue Pokémon",
            color: "from-purple-500 to-indigo-600",
            details: [
                "24 arènes thématiques",
                "Champions avec stratégies uniques",
                "Conseil des 4 et Maître",
                "Récompenses exclusives"
            ]
        }
    ];

    const systemFeatures = [
        {
            icon: <Coins />,
            title: "Économie Équilibrée",
            description: "Gagne des PokéCoins en jouant, complète des quêtes et participe à l'économie du serveur",
            gradient: "from-yellow-400 to-orange-500"
        },
        {
            icon: <Package />,
            title: "Élevage Avancé",
            description: "Système d'élevage complet avec héritage d'IVs, talents et Poké Balls personnalisées",
            gradient: "from-pink-500 to-rose-600"
        },
        {
            icon: <Star />,
            title: "Chasse aux Shinies",
            description: "Taux de Shinies améliorés, chaînes de capture et événements spéciaux augmentant tes chances",
            gradient: "from-cyan-400 to-blue-500"
        },
        {
            icon: <Shield />,
            title: "Protection Anti-Grief",
            description: "Système de claims pour protéger tes bases et ton ranch Pokémon des griefers",
            gradient: "from-green-500 to-teal-600"
        },
        {
            icon: <Users />,
            title: "Teams & Guildes",
            description: "Crée ou rejoins une team, participe aux guerres de territoires et événements de guilde",
            gradient: "from-indigo-500 to-purple-600"
        },
        {
            icon: <Wand2 />,
            title: "Objets Customs",
            description: "Objets uniques et exclusifs au serveur pour améliorer ton aventure",
            gradient: "from-red-500 to-orange-600"
        },
        {
            icon: <Heart />,
            title: "Bonheur & Amitié",
            description: "Système de bonheur pour débloquer les évolutions et bonus de combat",
            gradient: "from-pink-400 to-red-500"
        },
        {
            icon: <Book />,
            title: "Pokédex Complet",
            description: "Complète ton Pokédex et débloque des récompenses exclusives pour chaque milestone",
            gradient: "from-blue-500 to-indigo-600"
        },
        {
            icon: <Target />,
            title: "Quêtes Quotidiennes",
            description: "Quêtes journalières et hebdomadaires pour gagner des récompenses et progresser rapidement",
            gradient: "from-orange-500 to-red-600"
        }
    ];

    const specialFeatures = [
        {
            title: "Événements Légendaires",
            description: "Chaque semaine, des Pokémon légendaires apparaissent dans des zones spéciales. Forme ton équipe et affronte-les pour avoir une chance de les capturer !",
            icon: <Crown className="w-16 h-16" />,
            image: "/images/legendary-event.jpg"
        },
        {
            title: "Tournois PvP",
            description: "Participe aux tournois hebdomadaires avec des règles variées : Singles, Doubles, Monotype. Les meilleurs dresseurs remportent des prix exclusifs !",
            icon: <Trophy className="w-16 h-16" />,
            image: "/images/cobblemon-pvp.jpg"
        },
        {
            title: "Raids Dynamax",
            description: "Affronte des Pokémon Dynamax géants en coopération avec d'autres joueurs. Plus vous êtes nombreux, plus les récompenses sont importantes !",
            icon: <Zap className="w-16 h-16" />,
            image: "/images/dynamax.png"
        },
        {
            title: "Safari Zone",
            description: "Zone spéciale avec des Pokémon rares qui changent chaque mois. Explore et capture des Pokémon introuvables ailleurs !",
            icon: <Gift className="w-16 h-16" />,
            image: "/images/safari.jpg"
        }
    ];

    return (
        <div className="relative min-h-screen bg-white overflow-hidden">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800;900&family=Nunito:wght@400;600;700;800&display=swap');

                * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }

                body {
                    font-family: 'Nunito', sans-serif;
                    overflow-x: hidden;
                }

                .shine-effect {
                    position: relative;
                    overflow: hidden;
                }

                .shine-effect::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
                    transition: left 0.5s;
                }

                .shine-effect:hover::before {
                    left: 100%;
                }
            `}</style>

            <CustomCursor />

            {/* Navigation */}
            <motion.nav
                initial={{ y: -100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b-4 border-red-600 shadow-xl"
            >
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <motion.a
                        href="/"
                        className="flex items-center gap-4"
                        whileHover={{ scale: 1.05 }}
                    >
                        <div className="relative w-14 h-14">
                            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-red-600 to-red-700 shadow-2xl"></div>
                            <div className="absolute inset-0 top-0 h-1/2 rounded-t-full bg-red-600"></div>
                            <div className="absolute inset-0 bottom-0 top-1/2 h-1/2 rounded-b-full bg-white"></div>
                            <div className="absolute top-1/2 left-0 right-0 h-1 bg-gray-900 transform -translate-y-1/2"></div>
                            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-5 h-5 bg-white rounded-full border-2 border-gray-900"></div>
                        </div>
                        <div>
                            <div className="text-3xl font-black text-red-600" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                PokeMovie
                            </div>
                            <div className="text-xs font-bold text-gray-600 uppercase tracking-wider">Serveur Cobblemon</div>
                        </div>
                    </motion.a>

                    <motion.a
                        href="/"
                        className="flex items-center gap-2 px-6 py-3 rounded-full bg-red-600 text-white font-bold hover:bg-red-700 transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <Home className="w-5 h-5" />
                        Retour Accueil
                    </motion.a>
                </div>
            </motion.nav>

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-red-600 via-red-700 to-red-800 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-20 left-20 w-64 h-64 bg-white rounded-full blur-3xl"></div>
                    <div className="absolute bottom-20 right-20 w-96 h-96 bg-yellow-400 rounded-full blur-3xl"></div>
                </div>

                <div className="max-w-7xl mx-auto relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center"
                    >
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.3, type: "spring" }}
                            className="text-8xl mb-6"
                        >
                            ⚡
                        </motion.div>
                        <h1 className="text-6xl lg:text-8xl font-black text-white mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Fonctionnalités
                        </h1>
                        <p className="text-2xl text-red-100 max-w-3xl mx-auto font-semibold">
                            Découvre toutes les fonctionnalités qui font de PokeMovie le meilleur serveur Cobblemon français
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Main Features Grid */}
            <section className="py-24 px-6 bg-gray-50">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-5xl font-black text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Systèmes Principaux
                        </h2>
                        <p className="text-xl text-gray-600 font-semibold">
                            Les piliers de ton aventure Pokémon
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 gap-8">
                        {mainFeatures.map((feature, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                                whileHover={{ y: -10 }}
                                className="group relative bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all border-4 border-gray-200"
                            >
                                <div className={`absolute top-0 left-0 w-full h-2 rounded-t-3xl bg-gradient-to-r ${feature.color}`}></div>

                                <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform`}>
                                    {feature.icon}
                                </div>

                                <h3 className="text-3xl font-black text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                    {feature.title}
                                </h3>
                                <p className="text-lg text-gray-600 font-semibold mb-6">
                                    {feature.description}
                                </p>

                                <div className="space-y-3">
                                    {feature.details.map((detail, j) => (
                                        <div key={j} className="flex items-center gap-3">
                                            <ChevronRight className="w-5 h-5 text-red-600" />
                                            <span className="text-gray-700 font-semibold">{detail}</span>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* System Features */}
            <section className="py-24 px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-5xl font-black text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Systèmes Complémentaires
                        </h2>
                        <p className="text-xl text-gray-600 font-semibold">
                            Tout ce dont tu as besoin pour progresser
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {systemFeatures.map((feature, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.05 }}
                                whileHover={{ y: -8 }}
                                className="group bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all border-2 border-gray-200"
                            >
                                <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform`}>
                                    {feature.icon}
                                </div>
                                <h3 className="text-xl font-black text-gray-900 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                    {feature.title}
                                </h3>
                                <p className="text-gray-600 font-semibold">
                                    {feature.description}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Special Features */}
            <section className="py-24 px-6 bg-gradient-to-br from-gray-900 to-gray-800">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-5xl font-black text-white mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Événements Spéciaux
                        </h2>
                        <p className="text-xl text-gray-300 font-semibold">
                            Des événements réguliers pour ne jamais s'ennuyer
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 gap-8">
                        {specialFeatures.map((feature, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, x: i % 2 === 0 ? -50 : 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                                className="group relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl overflow-hidden shadow-2xl border-4 border-gray-700 hover:border-red-600 transition-all"
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-red-600/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>

                                <div className="relative h-64 overflow-hidden">
                                    <img
                                        src={feature.image}
                                        alt={feature.title}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent"></div>
                                    <div className="absolute bottom-4 left-4 text-white">
                                        {feature.icon}
                                    </div>
                                </div>

                                <div className="p-8">
                                    <h3 className="text-3xl font-black text-white mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                        {feature.title}
                                    </h3>
                                    <p className="text-gray-300 font-semibold text-lg">
                                        {feature.description}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="relative py-32 px-6 bg-gradient-to-br from-red-600 via-red-700 to-red-800">
                <div className="max-w-5xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="space-y-8"
                    >
                        <div className="text-7xl mb-6">🎮</div>
                        <h2 className="text-5xl lg:text-7xl font-black text-white mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Prêt à Commencer ?
                        </h2>
                        <p className="text-2xl text-red-100 max-w-3xl mx-auto font-semibold mb-12">
                            Rejoins-nous maintenant et vis l'aventure Pokémon ultime !
                        </p>

                        <motion.button
                            className="inline-flex items-center gap-4 px-12 py-6 rounded-2xl bg-white text-red-600 font-black text-2xl shine-effect hover:bg-yellow-400 hover:text-red-700 transition-all shadow-2xl"
                            style={{ fontFamily: 'Montserrat, sans-serif' }}
                            whileHover={{ scale: 1.05, y: -5 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <Play className="w-8 h-8" fill="currentColor" />
                            JOUER MAINTENANT
                        </motion.button>
                    </motion.div>
                </div>
            </section>

            {/* Footer */}
            <footer className="relative py-16 px-6 bg-gray-900 border-t-4 border-red-600">
                <div className="max-w-7xl mx-auto text-center">
                    <p className="text-gray-400 font-semibold">
                        © 2025 PokeMovie. Serveur Cobblemon non-officiel. Pokémon © Nintendo/Game Freak.
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default Features;